/**
 * Bestandscheck bij het in de winkelwagen leggen.
 *
 * Pure functies, zonder netwerk of DOM: de server levert alleen feiten over
 * het bestand (pixels, PDF-maat, afloopbevindingen, transparantie) en hier
 * volgt het oordeel voor het gekozen formaat. Zo kan de klant van formaat
 * wisselen zonder dat het bestand opnieuw gedownload wordt, en is er een
 * plek waar de drempels staan.
 *
 * De dpi-drempel is dezelfde ladder als in de ontwerpmodule
 * (`judgePrintRaster`): groen vanaf de vereiste dpi, rood onder twee derde
 * daarvan, ertussen oranje. Een upload mag niet strenger beoordeeld worden
 * dan een ontwerp uit de editor, anders wijst de check klanten af die de
 * editor gewoon door zou laten.
 *
 * Niets hiervan blokkeert bestellen. Het is advies met een snelle reparatie.
 */
import { judgePrintRaster, repairForLowDpi } from '@/lib/editor/print-resolution';
import { parseDimensions } from '@/lib/utils/dimensions';
import { isStencilProduct } from './stencil-islands';

export type FileCheckLevel = 'green' | 'orange' | 'red' | 'unchecked';

export type PdfBleedFindingCode = 'no-bleed' | 'insufficient-bleed' | 'trim-size-mismatch' | 'trimbox-missing' | 'flat-dieline-required' | 'white-edge';

/** Wat de server over het bestand weet. Alles optioneel: onbekend is geen fout. */
export type FileCheckFacts = {
  kind: 'image' | 'pdf' | 'vector' | 'unknown';
  /** De server kon het bestand lezen en meten. */
  checked: boolean;
  pixelWidth?: number | null;
  pixelHeight?: number | null;
  hasAlpha?: boolean | null;
  /** Kleurruimte zoals sharp die noemt: srgb, cmyk, b-w, rgb16, grey16, lab, multiband. */
  colorSpace?: string | null;
  /** Snijmaat van pagina 1 (TrimBox, anders MediaBox) in mm. */
  pageWidthMm?: number | null;
  pageHeightMm?: number | null;
  /** Bevindingen van `checkPdfBleed` voor het huidige formaat. */
  bleedFindings?: PdfBleedFindingCode[];
  hasCutline?: boolean | null;
  /** Sjablonen: aantal losse foliestukjes zonder brug (zie stencil-islands.ts); null = niet gemeten. */
  stencilIslands?: number | null;
};

export type FileCheckTarget = { widthMm: number; heightMm: number };

export type FileCheckIssueCode =
  | 'low_dpi'
  | 'aspect_mismatch'
  | 'orientation_mismatch'
  | 'phone_screenshot'
  | 'no_bleed'
  | 'insufficient_bleed'
  | 'size_mismatch'
  | 'flat_dieline'
  | 'pdf_scaled'
  | 'transparency'
  | 'color_space'
  | 'no_cutline'
  | 'stencil_islands'
  | 'editor_low_resolution'
  | 'not_checked';

export type FileCheckIssue = {
  code: FileCheckIssueCode;
  /** `tip` verandert het stoplicht niet; het is alleen een nuttige opmerking. */
  severity: 'red' | 'orange' | 'tip';
  dpi?: number;
  requiredDpi?: number;
  cropPercent?: number;
  /** Aantal losse binnenstukjes bij `stencil_islands`. */
  islands?: number;
};

export type FileCheckSizeOption = { value: string; label?: string };

export type FileCheckResult = {
  level: FileCheckLevel;
  dpi: number | null;
  requiredDpi: number | null;
  issues: FileCheckIssue[];
  /** De AI-vergroting (dynamisch, tot 8x) brengt de foto boven de drempel. */
  upscaleHelps: boolean;
  /** Grootste kleinere formaat waarop deze foto wel scherp wordt. */
  smallerSize: { value: string; label?: string; widthMm: number; heightMm: number; dpi: number } | null;
};

/** Slugs waar een transparante achtergrond juist de bedoeling is (snijden op contour). */
const TRANSPARENCY_EXPECTED = /sticker|label|etiket|decal|transfer|flex|flock|patch|button|badge/i;
const CUTLINE_PRODUCTS = new Set(['muursticker', 'vinylsticker', 'meubelsticker']);
const NORMAL_COLOR_SPACES = new Set(['srgb', 'rgb', 'rgb16', 'scrgb', 'cmyk', 'b-w', 'grey16', 'gray']);
/** Zelfde tolerantie als de oude bestandscheck: tot 4% verschil valt niet op. */
const ASPECT_TOLERANCE = 0.04;

function positive(value: unknown): number | null {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

/** Hoeveel procent van het beeld wegvalt als het het formaat vult. */
export function cropPercent(fileRatio: number, targetRatio: number) {
  const loss = 1 - Math.min(fileRatio, targetRatio) / Math.max(fileRatio, targetRatio);
  return Math.round(loss * 100);
}

function aspectDiff(fileRatio: number, targetRatio: number) {
  return Math.abs(fileRatio - targetRatio) / targetRatio;
}

/**
 * Effectieve dpi als het beeld het formaat vult (bijsnijden, niet vervormen):
 * de as die het minst pixels per inch heeft bepaalt de scherpte.
 */
/**
 * Herkent een schermafbeelding van een telefoon: staand, verhouding rond 9:19,5
 * en een breedte die precies een gangbare telefoonbreedte is (ook na 2x, 3x of
 * 4x opschalen). Zo'n bestand bevat statusbalk en zwarte balken, zoals de
 * banner van ORD-1792277966935-8V3GO3C7W.
 */
const PHONE_SCREEN_WIDTHS = [720, 750, 828, 1080, 1125, 1170, 1179, 1242, 1284, 1290, 1440];
export function looksLikePhoneScreenshot(pixelWidth: number, pixelHeight: number) {
  if (!(pixelWidth > 0) || !(pixelHeight > 0) || pixelWidth >= pixelHeight) return false;
  const ratio = pixelWidth / pixelHeight;
  if (ratio < 0.42 || ratio > 0.5) return false;
  return [1, 2, 3, 4].some((factor) => pixelWidth % factor === 0 && PHONE_SCREEN_WIDTHS.includes(pixelWidth / factor));
}

export function effectiveDpi(pixelWidth: number, pixelHeight: number, target: FileCheckTarget) {
  const dpiX = pixelWidth / (target.widthMm / 25.4);
  const dpiY = pixelHeight / (target.heightMm / 25.4);
  return Math.floor(Math.min(dpiX, dpiY));
}

/**
 * Kleurschaal voor resolutie (oktober 2026): goed en redelijk zijn allebei
 * groen; alleen zeer slecht (onder twee derde van de geadviseerde dpi, de
 * zware drempel in de editor) wordt oranje. Resolutie is nooit rood: het is
 * een advies, bestellen kan altijd.
 */
export function dpiLevel(dpi: number, requiredDpi: number): 'green' | 'orange' {
  if (dpi < Math.round((requiredDpi * 2) / 3)) return 'orange';
  return 'green';
}

export function targetFromSizeValue(sizeValue: unknown): FileCheckTarget | null {
  if (typeof sizeValue !== 'string' || !sizeValue) return null;
  const parsed = parseDimensions(sizeValue);
  if (!parsed || !(parsed.width > 0) || !(parsed.height > 0)) return null;
  return { widthMm: parsed.width, heightMm: parsed.height };
}

/**
 * Het grootste kleinere formaat uit de keuzelijst waarop de foto scherp
 * (groen) wordt, met ongeveer dezelfde verhouding als het huidige formaat of
 * als het bestand. Geen kandidaat: null, en dan toont de kaart die knop niet.
 */
export function suggestSmallerSize(
  pixelWidth: number,
  pixelHeight: number,
  current: FileCheckTarget,
  options: FileCheckSizeOption[],
): FileCheckResult['smallerSize'] {
  const currentArea = current.widthMm * current.heightMm;
  const currentRatio = current.widthMm / current.heightMm;
  const fileRatio = pixelWidth / pixelHeight;
  let best: FileCheckResult['smallerSize'] = null;
  for (const option of options) {
    const target = targetFromSizeValue(option.value);
    if (!target) continue;
    // Het huidige formaat (ook gedraaid) is nooit een "kleiner" voorstel.
    const same = (Math.abs(target.widthMm - current.widthMm) <= 1 && Math.abs(target.heightMm - current.heightMm) <= 1)
      || (Math.abs(target.widthMm - current.heightMm) <= 1 && Math.abs(target.heightMm - current.widthMm) <= 1);
    if (same) continue;
    const area = target.widthMm * target.heightMm;
    if (area >= currentArea - 1) continue;
    const ratio = target.widthMm / target.heightMm;
    if (aspectDiff(ratio, currentRatio) > ASPECT_TOLERANCE && aspectDiff(ratio, fileRatio) > ASPECT_TOLERANCE) continue;
    const dpi = effectiveDpi(pixelWidth, pixelHeight, target);
    const { requiredDpi } = judgePrintRaster(dpi, target.widthMm, target.heightMm);
    if (dpi < requiredDpi) continue;
    if (!best || area > best.widthMm * best.heightMm) {
      best = { value: option.value, ...(option.label ? { label: option.label } : {}), widthMm: target.widthMm, heightMm: target.heightMm, dpi };
    }
  }
  return best;
}

/**
 * Komt dit bestand uit de editor? Een gewone upload kan ook designData hebben
 * (studiomateriaal, bronbestand, EPS-omzetting); een editorontwerp heeft het
 * canvas (`json`), een drukbestand, pagina's of een eigen dpi-oordeel.
 */
export function isEditorDesign(designData: unknown): boolean {
  if (!designData || typeof designData !== 'object' || Array.isArray(designData)) return false;
  const data = designData as Record<string, unknown>;
  return Boolean(data.json || data.printFile || data.pageDesigns || data.productionReadiness || data.storageUrl || data.printful);
}

type EditorReadiness = { reason?: unknown; dpi?: unknown; requiredDpi?: unknown } | null | undefined;

function levelOf(issues: FileCheckIssue[]): FileCheckLevel {
  if (issues.some((issue) => issue.severity === 'red')) return 'red';
  if (issues.some((issue) => issue.severity === 'orange')) return 'orange';
  return 'green';
}

/**
 * Het oordeel. `editorReadiness` is `designData.productionReadiness` van een
 * ontwerp uit de editor: die heeft de geplaatste foto's al zelf gemeten, en
 * de maat en afloop van een editorexport kloppen per definitie.
 */
export function evaluatePrintFile(input: {
  facts: FileCheckFacts | null;
  target: FileCheckTarget | null;
  productSlug?: string | null;
  sizeOptions?: FileCheckSizeOption[];
  fromEditor?: boolean;
  editorReadiness?: EditorReadiness;
}): FileCheckResult {
  const { facts, target, productSlug = '', sizeOptions = [], fromEditor = false, editorReadiness } = input;
  const empty = { dpi: null, requiredDpi: null, upscaleHelps: false, smallerSize: null };

  if (fromEditor) {
    if (editorReadiness?.reason === 'LOW_RESOLUTION') {
      const dpi = positive(editorReadiness.dpi);
      const requiredDpi = positive(editorReadiness.requiredDpi);
      const rounded = { dpi: dpi ? Math.round(dpi) : null, requiredDpi: requiredDpi ? Math.round(requiredDpi) : null };
      // Redelijk telt als goed; alleen zeer slecht (of onbekend) is een
      // melding, en die is oranje: bestellen kan gewoon.
      if (dpi && requiredDpi && dpiLevel(dpi, requiredDpi) === 'green') return { ...empty, ...rounded, level: 'green', issues: [] };
      return {
        ...empty,
        ...rounded,
        level: 'orange',
        issues: [{ code: 'editor_low_resolution', severity: 'orange', ...(rounded.dpi ? { dpi: rounded.dpi } : {}), ...(rounded.requiredDpi ? { requiredDpi: rounded.requiredDpi } : {}) }],
      };
    }
    return { ...empty, level: 'green', issues: [] };
  }

  if (!facts || !facts.checked || facts.kind === 'vector' || facts.kind === 'unknown') {
    return { ...empty, level: 'unchecked', issues: [{ code: 'not_checked', severity: 'tip' }] };
  }

  const issues: FileCheckIssue[] = [];
  let dpi: number | null = null;
  let requiredDpi: number | null = null;
  let upscaleHelps = false;
  let smallerSize: FileCheckResult['smallerSize'] = null;

  if (facts.kind === 'image') {
    const width = positive(facts.pixelWidth);
    const height = positive(facts.pixelHeight);
    if (!width || !height) return { ...empty, level: 'unchecked', issues: [{ code: 'not_checked', severity: 'tip' }] };
    if (target) {
      dpi = effectiveDpi(width, height, target);
      requiredDpi = judgePrintRaster(dpi, target.widthMm, target.heightMm).requiredDpi;
      const level = dpiLevel(dpi, requiredDpi);
      if (level !== 'green') {
        issues.push({ code: 'low_dpi', severity: level, dpi, requiredDpi });
        upscaleHelps = repairForLowDpi(dpi, requiredDpi)?.kind === 'upscale';
        smallerSize = suggestSmallerSize(width, height, target, sizeOptions);
      }
      const fileRatio = width / height;
      const targetRatio = target.widthMm / target.heightMm;
      if (looksLikePhoneScreenshot(width, height) && aspectDiff(fileRatio, targetRatio) > ASPECT_TOLERANCE) {
        issues.push({ code: 'phone_screenshot', severity: 'orange', cropPercent: cropPercent(fileRatio, targetRatio) });
      } else if (aspectDiff(fileRatio, targetRatio) > ASPECT_TOLERANCE) {
        const rotatedFits = aspectDiff(fileRatio, 1 / targetRatio) <= ASPECT_TOLERANCE && Math.abs(targetRatio - 1) > ASPECT_TOLERANCE;
        issues.push(rotatedFits
          ? { code: 'orientation_mismatch', severity: 'orange', cropPercent: cropPercent(fileRatio, targetRatio) }
          : { code: 'aspect_mismatch', severity: 'orange', cropPercent: cropPercent(fileRatio, targetRatio) });
      }
    }
    if (facts.hasAlpha === true && !TRANSPARENCY_EXPECTED.test(String(productSlug || ''))) {
      issues.push({ code: 'transparency', severity: 'tip' });
    }
    const space = String(facts.colorSpace || '').toLowerCase();
    if (space && !NORMAL_COLOR_SPACES.has(space)) issues.push({ code: 'color_space', severity: 'orange' });
  }

  if (facts.kind === 'pdf') {
    const findings = new Set(facts.bleedFindings || []);
    if (findings.has('flat-dieline-required')) issues.push({ code: 'flat_dieline', severity: 'red' });
    if (findings.has('no-bleed')) issues.push({ code: 'no_bleed', severity: 'orange' });
    else if (findings.has('insufficient-bleed')) issues.push({ code: 'insufficient_bleed', severity: 'orange' });
    if (findings.has('trim-size-mismatch')) issues.push({ code: 'size_mismatch', severity: 'orange' });
    const pageWidth = positive(facts.pageWidthMm);
    const pageHeight = positive(facts.pageHeightMm);
    // Zonder afloopregels van de leverancier alleen de verhouding en maat
    // naast het bestelde formaat leggen; een vector-PDF schaalt zonder verlies.
    if (target && pageWidth && pageHeight && !findings.size) {
      const fileRatio = pageWidth / pageHeight;
      const targetRatio = target.widthMm / target.heightMm;
      if (aspectDiff(fileRatio, targetRatio) > ASPECT_TOLERANCE) {
        const rotatedFits = aspectDiff(fileRatio, 1 / targetRatio) <= ASPECT_TOLERANCE && Math.abs(targetRatio - 1) > ASPECT_TOLERANCE;
        issues.push({ code: rotatedFits ? 'orientation_mismatch' : 'aspect_mismatch', severity: 'orange', cropPercent: cropPercent(fileRatio, targetRatio) });
      } else if (Math.abs(pageWidth - target.widthMm) / target.widthMm > 0.02) {
        issues.push({ code: 'pdf_scaled', severity: 'tip' });
      }
    }
    if (CUTLINE_PRODUCTS.has(String(productSlug || '')) && facts.hasCutline === false) {
      issues.push({ code: 'no_cutline', severity: 'orange' });
    }
  }

  const islands = isStencilProduct(productSlug) ? positive(facts.stencilIslands) : null;
  if (islands) issues.push({ code: 'stencil_islands', severity: 'orange', islands });

  return { level: levelOf(issues), dpi, requiredDpi, issues, upscaleHelps, smallerSize };
}

/** Korte, stabiele lijst codes voor analytics: geen bestandsnamen of maten. */
export function fileCheckIssueCodes(result: Pick<FileCheckResult, 'issues'>) {
  return Array.from(new Set(result.issues.map((issue) => issue.code))).sort();
}
